import type { Service } from "~/service/models/Service";
import { getStepPrice } from "./getStepPrice";

type ServicePricing = Service["pricings"][number];

const PRICE_INTERVAL = 1;

export function getPriceByEstimatedTime(
  estimatedTime: number,
  service: Service,
) {
  const matchingPricings = service.pricings.filter(
    (pricing) => estimatedTime >= pricing.minTime,
  );

  const sortedPricings = matchingPricings.sort((a, b) => a.minTime - b.minTime);

  const total = sortedPricings.reduce((totalPrice, pricing, i) => {
    const nextPricing = sortedPricings[i + 1];
    const tierTime = nextPricing
      ? nextPricing.minTime - pricing.minTime
      : estimatedTime - pricing.minTime;

    switch (pricing.type) {
      case "fixed": {
        const estimatedPrice = calcFixed(tierTime, pricing);
        return totalPrice + estimatedPrice;
      }
      case "durationBased": {
        const estimatedPrice = calcDurationBased(tierTime, pricing);
        return totalPrice + estimatedPrice;
      }
      default: {
        throw new Error(`Unsupported pricing type: ${pricing}`);
      }
    }
  }, 0);

  return round(total, PRICE_INTERVAL);
}

function round(num: number, interval = 1) {
  return Math.round(num / interval) * interval;
}

type DurationBasedPricing = Extract<ServicePricing, { type: "durationBased" }>;

function calcDurationBased(
  estimatedTime: number,
  pricing: DurationBasedPricing,
) {
  /**
   * Intentionally rounding upwards since it is charging per each started step.
   **/
  const stepCount = Math.ceil(estimatedTime / pricing.step);
  const stepPrice = getStepPrice(pricing);
  return stepCount * stepPrice;
}

type FixedPricing = Extract<ServicePricing, { type: "fixed" }>;

function calcFixed(_estimatedTime: number, pricing: FixedPricing) {
  return pricing.amount;
}
